import React, { useEffect, useState } from "react";
import SectionTitle from "./SectionTitle";
import { useHome } from "../api/queries";
import { Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { type } from "../lib/typography";

export default function Testimonials() {
  const { data, isLoading } = useHome();
  const testimonials = data?.reviews ?? [];

  const [i, setI] = useState(0);

  useEffect(() => {
    setI(0);
  }, [testimonials.length]);

  useEffect(() => {
    if (testimonials.length <= 1) return undefined;
    const t = setInterval(() => setI((p) => (p + 1) % testimonials.length), 7000);
    return () => clearInterval(t);
  }, [testimonials.length]);

  if (isLoading) {
    return (
      <section className="relative py-24 bg-gradient-to-br from-[#e8f2fc] via-white to-slate-50">
        <div className="max-w-5xl mx-auto px-6 text-center animate-pulse">
          <div className="h-8 bg-slate-200 rounded w-48 mx-auto mb-4" />
          <div className="h-12 bg-slate-200 rounded w-2/3 mx-auto" />
          <div className="mt-14 space-y-4">
            <div className="h-24 bg-slate-100 rounded max-w-3xl mx-auto" />
            <div className="h-4 bg-slate-200 rounded w-40 mx-auto" />
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) return null;

  const active = testimonials[i];

  return (
    <section className="relative py-24 bg-gradient-to-br from-[#e8f2fc] via-white to-slate-50">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <SectionTitle
          align="center"
          eyebrow="Client Voices"
          title="What our clients say"
        />
        <div className="mt-14 relative">
          <Quote className="w-20 h-20 text-[#cce4fb] mx-auto mb-6" />
          <p
            key={`q-${active.id}-${i}`}
            className={`${type.lead} text-slate-700 italic max-w-3xl mx-auto animate-fade-up`}
          >
            “{active.quote}”
          </p>
          <div key={`a-${active.id}-${i}`} className="mt-8 animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <div className={`${type.subsectionTitle} text-[#0a1a2f]`}>{active.author}</div>
            <div className={`${type.label} text-[#1758a8] mt-1`}>
              {active.company}
            </div>
          </div>

          {testimonials.length > 1 && (
            <div className="mt-10 flex items-center justify-center gap-4">
              <button
                onClick={() => setI((p) => (p - 1 + testimonials.length) % testimonials.length)}
                className="w-11 h-11 rounded-full border border-slate-300 hover:border-[#1e6fcf] hover:bg-[#1e6fcf] hover:text-white text-slate-600 flex items-center justify-center transition-all"
                aria-label="Previous"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex gap-2">
                {testimonials.map((tItem, idx) => (
                  <button
                    key={tItem.id}
                    onClick={() => setI(idx)}
                    className={`h-2 transition-all rounded-full ${
                      idx === i ? "w-10 bg-[#1e6fcf]" : "w-2 bg-slate-300"
                    }`}
                    aria-label={`Testimonial ${idx + 1}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setI((p) => (p + 1) % testimonials.length)}
                className="w-11 h-11 rounded-full border border-slate-300 hover:border-[#1e6fcf] hover:bg-[#1e6fcf] hover:text-white text-slate-600 flex items-center justify-center transition-all"
                aria-label="Next"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
